import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useFarmThresholds, type FarmThresholdsData } from '@/hooks/useFarmThresholds';
import { api } from '@/lib/api';
import { saveFailureMessage } from '@/lib/apiErrors';
import { cn } from '@/lib/cn';
import { qk } from '@/lib/queryKeys';
import { GRID_ROWS } from '@/lib/waterModel';
import { BAND_FIELDS, type Band, type BandField } from '@/lib/thresholds';

const COLUMN_LABEL: Record<BandField, string> = {
  crit_min: 'Red below',
  rec_min: 'Yellow below',
  rec_max: 'Yellow above',
  crit_max: 'Red above',
};

// The parameters the entry grid colours. A pair is one row: alkalinity's band judges both its numbers
// (the second follows the first, stored as alkalinity_2); only the calcium number of Cal/Mag has a band.
const PAIR_NOTE: Record<string, string> = {
  alkalinity_ppm: 'The band applies to both numbers (x/y)',
  calcium_ppm: 'The band applies to the calcium number',
};
const ROWS = GRID_ROWS.filter((p) => p.type === 'number').map((p) => ({
  column: p.dbColumn,
  label: p.label,
  unit: p.unit,
  note: PAIR_NOTE[p.dbColumn],
}));

type Draft = Record<string, Record<BandField, string>>;

const show = (n: number | null | undefined) => (n === null || n === undefined ? '' : String(n));

function toDraft(effective: FarmThresholdsData['effective']): Draft {
  const out: Draft = {};
  for (const r of ROWS) {
    const band = effective[r.column];
    if (band) out[r.column] = Object.fromEntries(BAND_FIELDS.map((f) => [f, show(band[f])])) as Record<BandField, string>;
  }
  return out;
}

function parse(text: string): number | null | 'bad' {
  const t = text.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : 'bad';
}

function rowError(row: Record<BandField, string>): string | null {
  const v = BAND_FIELDS.map((f) => parse(row[f]));
  if (v.includes('bad')) return 'Type a number, or leave blank for no limit';
  const [critMin, recMin, recMax, critMax] = v as Array<number | null>;
  if (recMin !== null && recMax !== null && !(recMin < recMax)) return 'Yellow low must be below yellow high';
  const set = [critMin, recMin, recMax, critMax].filter((x): x is number => x !== null);
  for (let i = 0; i < set.length; i++) for (let j = i + 1; j < set.length; j++) if (set[i] > set[j]) return 'Red limits sit outside the yellow ones';
  return null;
}

/** Only what differs from the default is stored (sparse); a field blanked where the default has a
 *  limit is stored as null, meaning "no limit". Conditions on a band are carried over untouched. */
function buildOverrides(draft: Draft, data: FarmThresholdsData): Record<string, Record<string, unknown>> {
  const out: Record<string, Record<string, unknown>> = {};
  for (const r of ROWS) {
    const row = draft[r.column];
    const def = data.defaults[r.column];
    if (!row || !def) continue;
    const band: Record<string, unknown> = {};
    for (const f of BAND_FIELDS) {
      const value = parse(row[f]) as number | null;
      if (value !== (def[f] ?? null)) band[f] = value;
    }
    const conditions = data.overrides[r.column]?.conditions;
    if (conditions) band.conditions = conditions;
    if (Object.keys(band).length > 0) out[r.column] = band;
  }
  // The save replaces the whole map: keep overrides this table has no row for (e.g. alkalinity_2).
  const shownColumns = new Set(ROWS.map((r) => r.column));
  for (const [column, override] of Object.entries(data.overrides)) {
    if (!shownColumns.has(column)) out[column] = override as Record<string, unknown>;
  }
  return out;
}

export function ParameterThresholdsEditor({ farmId, canEdit }: { farmId: string; canEdit: boolean }) {
  const { data, isLoading, isError } = useFarmThresholds(farmId);
  if (isLoading) {
    return (
      <Card>
        <Card.Body>
          <LoadingSpinner />
        </Card.Body>
      </Card>
    );
  }
  if (isError || !data) {
    return (
      <Card>
        <Card.Body>
          <p className="text-sm text-gray-500">Couldn't load the entry thresholds. Reload the page.</p>
        </Card.Body>
      </Card>
    );
  }
  // Re-key on the server's version so a save (or another owner's) resets the draft.
  return <Editor key={JSON.stringify(data.effective)} farmId={farmId} data={data} canEdit={canEdit} />;
}

function Editor({ farmId, data, canEdit }: { farmId: string; data: FarmThresholdsData; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Draft>(() => toDraft(data.effective));
  const baseline = useMemo(() => toDraft(data.effective), [data.effective]);
  const errors = ROWS.map((r) => (draft[r.column] ? rowError(draft[r.column]) : null));
  const dirty = JSON.stringify(draft) !== JSON.stringify(baseline);

  const save = useMutation({
    mutationFn: (parameter_thresholds: Record<string, Record<string, unknown>>) =>
      api.put(`/api/v1/farms/${farmId}/settings`, { parameter_thresholds }),
    onSuccess: () => {
      toast.success('Entry thresholds saved');
      return queryClient.invalidateQueries({ queryKey: qk.thresholds.farm(farmId) });
    },
  });

  const set = (column: string, field: BandField, value: string) =>
    setDraft((d) => ({ ...d, [column]: { ...d[column], [field]: value } }));
  const reset = (column: string) => setDraft((d) => ({ ...d, [column]: toDraft(data.defaults)[column] }));
  const isDefault = (column: string) => JSON.stringify(draft[column]) === JSON.stringify(toDraft(data.defaults)[column]);
  const hint = (column: string) => {
    const b: Band | undefined = data.defaults[column];
    return b ? BAND_FIELDS.map((f) => show(b[f]) || '–').join(' · ') : '';
  };

  return (
    <Card>
      <Card.Header>
        <Card.Title>Entry thresholds</Card.Title>
      </Card.Header>
      <Card.Body>
        <p className="mb-3 text-sm text-gray-500">
          Colours on the water entry screens and the warnings when a reading is saved. A reading outside the yellow band is
          yellow; outside the red band it is red. Leave a box blank for no limit on that side.
          {!canEdit && ' Only a Farm Owner can change these.'}
        </p>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse text-sm" data-thresholds-table>
            <thead>
              <tr className="text-left text-xs text-gray-500">
                <th className="py-1.5 pr-3 font-medium">Parameter</th>
                {BAND_FIELDS.map((f) => (
                  <th key={f} className="px-1.5 py-1.5 font-medium">
                    {COLUMN_LABEL[f]}
                  </th>
                ))}
                <th className="py-1.5 pl-2 font-medium" />
              </tr>
            </thead>
            <tbody>
              {ROWS.map((r, i) => {
                const row = draft[r.column];
                if (!row) return null;
                return (
                  <tr key={r.column} className="border-t border-gray-100 align-top" data-threshold-row={r.column}>
                    <td className="py-2 pr-3">
                      <div className="font-medium text-gray-900">{r.label}</div>
                      <div className="text-xs text-gray-400">
                        {r.unit ? `${r.unit} · ` : ''}default {hint(r.column)}
                      </div>
                      {r.note && <div className="text-xs text-gray-500">{r.note}</div>}
                      {errors[i] && (
                        <div role="alert" className="mt-1 text-xs text-red-600">
                          {errors[i]}
                        </div>
                      )}
                    </td>
                    {BAND_FIELDS.map((f) => (
                      <td key={f} className="px-1.5 py-1.5">
                        <input
                          inputMode="decimal"
                          value={row[f]}
                          disabled={!canEdit}
                          onChange={(e) => set(r.column, f, e.target.value)}
                          aria-label={`${r.label} ${COLUMN_LABEL[f].toLowerCase()}`}
                          placeholder="no limit"
                          className={cn(
                            'min-h-[44px] w-20 rounded-md border px-2 text-sm tabular-nums disabled:bg-gray-50 disabled:text-gray-500',
                            errors[i] ? 'border-red-400' : 'border-gray-300',
                            f.startsWith('crit') ? 'focus:border-red-500' : 'focus:border-amber-500'
                          )}
                        />
                      </td>
                    ))}
                    <td className="py-1.5 pl-2 text-right">
                      {canEdit && !isDefault(r.column) && (
                        <button type="button" onClick={() => reset(r.column)} className="min-h-[44px] min-w-[44px] text-xs font-semibold text-primary-600">
                          Reset
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {canEdit && (
          <div className="mt-4 flex items-center justify-end gap-3">
            {save.isError && (
              <span role="alert" className="text-sm text-red-600">
                Not saved: {saveFailureMessage(save.error, 'try again')}
              </span>
            )}
            <Button
              onClick={() => save.mutate(buildOverrides(draft, data))}
              disabled={!dirty || errors.some(Boolean) || save.isPending}
              loading={save.isPending}
              className="min-h-[44px]"
            >
              Save entry thresholds
            </Button>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
